import React, { useState } from 'react';
import { Eye, EyeOff, User, Phone, Mail, MapPin, Lock, Check, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
import Swal from 'sweetalert2';
import UniversalNavbar from './UniversalNavbar';
import VFooter from './VFooter';
import Button from './ui/Button';
import Input from './ui/Input';
import Alert from './ui/Alert';
import Modal from './ui/Modal';

const RegistrationForm = () => {
  const [formData, setFormData] = useState({
    username: '',
    contact_number: '',
    email: '',
    pincode: '',
    country: '',
    state: '',
    city: '',
    address: '',
    address2: '',
    password: '',
    confirm_password: '',
    termsAccepted: false,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [pincodeError, setPincodeError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const validatePassword = () => {
    const { password, confirm_password } = formData;
    if (password.length < 8) {
      setErrorMessage('Password must be at least 8 characters long.');
      return false;
    }
    if (password !== confirm_password) {
      setErrorMessage('Passwords do not match.');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    if (!validatePassword()) return;

    setIsLoading(true);
    try {
      const response = await fetch('https://vasstrinfo.com/register/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();
      if (data.success) {
        Swal.fire({
          icon: 'success',
          title: 'Registration Successful',
          text: data.message,
          confirmButtonColor: '#d25614'
        }).then(() => {
          window.location.href = '/login';
        });
      } else {
        setErrorMessage(data.error);
      }
    } catch (error) {
      console.error('Error:', error);
      setErrorMessage('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAutofill = async () => {
    if (formData.pincode.length === 6) {
      try {
        const response = await fetch(`https://api.postalpincode.in/pincode/${formData.pincode}`);
        const data = await response.json();
        if (data[0].Status === 'Success') {
          const { District, State, Country } = data[0].PostOffice[0];
          setFormData((prev) => ({
            ...prev,
            city: District,
            state: State,
            country: Country,
          }));
          setPincodeError(false);
        } else {
          setPincodeError(true);
        }
      } catch (error) {
        console.error('Error fetching pincode details:', error);
        setPincodeError(true);
      }
    } else {
      setPincodeError(true);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5, ease: "easeOut" }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-light via-white to-primary-light/50 font-fell">
      <UniversalNavbar />

      <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center px-4 py-2 bg-primary/10 rounded-full text-primary font-semibold text-sm mb-4">
              <span className="w-2 h-2 bg-primary rounded-full mr-2 animate-pulse"></span>
              Join Our Designer Community
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold text-dark mb-4">
              Create Your <span className="text-primary">Designer Account</span>
            </h1>
            <p className="text-lg text-muted max-w-2xl mx-auto">
              Start showcasing your designs and connect with customers across India
            </p>
          </motion.div>

          {/* Registration Form Card */}
          <motion.div
            className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <div className="p-8 lg:p-12">
              {errorMessage && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-6"
                >
                  <Alert variant="error" icon>
                    {errorMessage}
                  </Alert>
                </motion.div>
              )}

              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Personal Information Section */}
                <motion.div variants={itemVariants}>
                  <h3 className="text-lg font-semibold text-dark mb-4 flex items-center">
                    <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center mr-3">
                      <span className="text-primary font-bold text-sm">1</span>
                    </div>
                    Personal Information
                  </h3>
                  <div className="space-y-6">
                    <Input
                      type="text"
                      name="username"
                      id="username"
                      label="Username"
                      placeholder="Enter your username"
                      value={formData.username}
                      onChange={handleChange}
                      icon={<User size={18} />}
                      required
                    />

                    {/* Contact and Email - Side by side on desktop */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <Input
                        type="tel"
                        name="contact_number"
                        id="contact_number"
                        label="Contact Number"
                        placeholder="Phone number"
                        value={formData.contact_number}
                        onChange={handleChange}
                        icon={<Phone size={18} />}
                        required
                      />
                      <Input
                        type="email"
                        name="email"
                        id="email"
                        label="Email"
                        placeholder="Enter your email"
                        value={formData.email}
                        onChange={handleChange}
                        icon={<Mail size={18} />}
                        required
                      />
                    </div>
                  </div>
                </motion.div>

                {/* Address Information Section */}
                <motion.div variants={itemVariants}>
                  <h3 className="text-lg font-semibold text-dark mb-4 flex items-center">
                    <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center mr-3">
                      <span className="text-primary font-bold text-sm">2</span>
                    </div>
                    Address Information
                  </h3>
                  <div className="space-y-6">
                    {/* Pincode with Autofill */}
                    <div>
                      <label htmlFor="pincode" className="block text-sm font-medium text-[var(--color-gray-700)] mb-2">
                        Pincode
                      </label>
                      <div className="flex gap-3">
                        <Input
                          type="text"
                          name="pincode"
                          id="pincode"
                          placeholder="6-digit pincode"
                          value={formData.pincode}
                          onChange={handleChange}
                          icon={<MapPin size={18} />}
                          error={pincodeError ? 'Please enter a valid 6-digit pincode' : ''}
                          required
                          className="flex-1"
                        />
                        <Button
                          type="button"
                          onClick={handleAutofill}
                          variant="accent"
                          className="whitespace-nowrap"
                        >
                          Autofill
                        </Button>
                      </div>
                    </div>

                    {/* Location Fields - 3 columns on desktop, stacked on mobile */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <Input
                        type="text"
                        name="country"
                        id="country"
                        label="Country"
                        placeholder="Country"
                        value={formData.country}
                        onChange={handleChange}
                        required
                      />
                      <Input
                        type="text"
                        name="state"
                        id="state"
                        label="State"
                        placeholder="State"
                        value={formData.state}
                        onChange={handleChange}
                        required
                      />
                      <Input
                        type="text"
                        name="city"
                        id="city"
                        label="City"
                        placeholder="City"
                        value={formData.city}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    <Input
                      type="text"
                      name="address"
                      id="address"
                      label="Address Line 1"
                      placeholder="Street address"
                      value={formData.address}
                      onChange={handleChange}
                      required
                    />

                    <Input
                      type="text"
                      name="address2"
                      id="address2"
                      label="Address Line 2 (Optional)"
                      placeholder="Apartment, suite, etc."
                      value={formData.address2}
                      onChange={handleChange}
                    />
                  </div>
                </motion.div>

                {/* Security Information Section */}
                <motion.div variants={itemVariants}>
                  <h3 className="text-lg font-semibold text-dark mb-4 flex items-center">
                    <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center mr-3">
                      <span className="text-primary font-bold text-sm">3</span>
                    </div>
                    Security Information
                  </h3>
                  <div className="space-y-6">
                    {/* Password Fields - Side by side on desktop */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        id="password"
                        label="Password"
                        placeholder="Min 8 characters"
                        value={formData.password}
                        onChange={handleChange}
                        icon={<Lock size={18} />}
                        iconRight={
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="text-[var(--color-gray-500)] hover:text-[var(--color-gray-700)] transition-colors"
                          >
                            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                          </button>
                        }
                        required
                      />
                      <Input
                        type={showConfirmPassword ? 'text' : 'password'}
                        name="confirm_password"
                        id="confirm_password"
                        label="Confirm Password"
                        placeholder="Confirm password"
                        value={formData.confirm_password}
                        onChange={handleChange}
                        icon={<Lock size={18} />}
                        iconRight={
                          <button
                            type="button"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            className="text-[var(--color-gray-500)] hover:text-[var(--color-gray-700)] transition-colors"
                          >
                            {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                          </button>
                        }
                        required
                      />
                    </div>

                    {/* Terms and Conditions */}
                    <div className="flex items-start p-4 bg-gray-50 rounded-xl">
                      <div className="flex items-center h-5">
                        <input
                          type="checkbox"
                          id="termsAccepted"
                          name="termsAccepted"
                          checked={formData.termsAccepted}
                          onChange={handleChange}
                          required
                          className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                        />
                      </div>
                      <div className="ml-3 text-sm">
                        <label htmlFor="termsAccepted" className="font-medium text-gray-700">
                          I agree to the{' '}
                          <button
                            type="button"
                            onClick={() => setShowModal(true)}
                            className="text-primary hover:text-primary-dark underline transition-colors duration-300"
                          >
                            terms and conditions
                          </button>
                        </label>
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Submit Section */}
                <motion.div variants={itemVariants} className="space-y-6">
                  <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    fullWidth
                    loading={isLoading}
                    icon={<Sparkles size={20} />}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Creating Account...' : 'Create My Designer Account'}
                  </Button>

                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-[var(--color-gray-300)]" />
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2 bg-white text-[var(--color-gray-500)]">Already have an account?</span>
                    </div>
                  </div>

                  <div className="text-center">
                    <a href="/login" className="font-medium text-[var(--color-primary)] hover:text-[var(--color-primary-light)] transition-colors duration-300 text-lg">
                      Sign in to your account
                    </a>
                  </div>
                </motion.div>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
      <VFooter />

      {/* Terms and Conditions Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title="Terms and Conditions"
        size="lg"
      >
        <div className="space-y-4 text-sm max-h-[60vh] overflow-y-auto">
              <h4 className="font-semibold">1. Partner Responsibilities</h4>
              <p><strong>1.1 Uploading Content</strong> You are responsible for uploading product listings to the Vasstr platform. You agree that no personal information, including photos of individuals, will be included in your product listings unless specifically required by Vasstr for the purposes of the listing.</p>
              <p><strong>1.2 Product Delivery</strong> You will deliver your products to the Vasstr warehouse for storage and order fulfillment. Once a customer places an order, Vasstr will manage the delivery to the customer, and you will be responsible for ensuring the products are received in good condition at the warehouse.</p>
              <p><strong>1.3 Order Fulfillment and Responsibility</strong> Upon delivery of your products to customers, you will ensure that they are free from defects or damage. In the case that a product is defective or damaged, you will be responsible for offering replacements or repairs at your expense, in accordance with Vasstr’s no-returns policy.</p>
              <p><strong>1.4 No Returns Policy</strong> Vasstr enforces a no-returns policy, except in cases where products are defective or damaged. You are responsible for resolving any issues related to damaged or defective goods and ensuring customer satisfaction.</p>
              <p><strong>1.5 Adherence to Policy</strong> You agree to comply with all relevant policies, including the protection of personal information. You will not share any personal information with the public unless explicitly required for order fulfillment or payment processing.</p>
              <h4 className="font-semibold">2. Vasstr Responsibilities</h4>
              <p><strong>2.1 Platform Support</strong> Vasstr will provide a secure and functional platform for product listings, order processing, and customer communications.</p>
              <p><strong>2.2 Payment Security</strong> Vasstr ensures that all payments are securely processed through industry-standard security protocols to protect financial data for both the Partner and customers.</p>
              <p><strong>2.3 Customer Payment</strong> Vasstr will transfer the payment for each successfully fulfilled order to the Partner, after deducting the applicable fees (such as payment gateway fees). Payments will be made once the customer confirms receipt of the product.</p>
              <p><strong>2.4 Personal Information Security</strong> Vasstr is responsible for ensuring the security of all personal information collected from customers or partners. This includes safeguarding sensitive data from breaches and ensuring compliance with data protection laws.</p>
              <p><strong>2.5 No Returns Policy</strong> Vasstr enforces a no-returns policy unless a product is damaged or defective. In case of a damaged product, Vasstr will work with the Partner to resolve the issue promptly.</p>
              <h4 className="font-semibold">3. Revenue Sharing</h4>
              <p>Payment Gateway Fee: From the outset, including during the testing phase, Vasstr will charge a 3% payment gateway fee on each transaction to cover payment processing costs.</p>
              <p>Fee Changes: After the testing phase, Vasstr may increase this fee. You will be notified in advance and, if you agree to the new terms, a new agreement will be signed. If you do not agree, you have the option to terminate the partnership.</p>
              <h4 className="font-semibold">4. Confidentiality</h4>
              <p>Both Vasstr and the Partner agree to keep all sensitive information, including business strategies, design details, and pricing, confidential. This obligation continues even after the termination of the collaboration.</p>
              <h4 className="font-semibold">5. Intellectual Property</h4>
              <p>You retain full ownership of your designs and products. By using the Vasstr platform, you grant Vasstr a limited, non-exclusive license to use your designs solely for the purpose of displaying and promoting your products on the platform.</p>
              <h4 className="font-semibold">6. Payment Process</h4>
              <p>Once the customer confirms receipt of the product, Vasstr will release payment to the Partner within a reasonable timeframe, after deducting the applicable payment gateway fee or any other applicable charges.<br />Payments are processed securely and in accordance with industry-standard security protocols.</p>
              <h4 className="font-semibold">7. Data Security</h4>
              <p>Vasstr is responsible for ensuring the security and confidentiality of all personal and financial data collected from customers and Partners. This includes protecting against data breaches and ensuring compliance with data protection laws.</p>
              <h4 className="font-semibold">8. Term and Termination</h4>
              <p>This agreement is effective until either party provides 30 days’ written notice of termination, or until a formal partnership agreement is signed. Either party may terminate this agreement if the other party fails to meet its obligations as outlined in these terms and conditions.</p>
              <h4 className="font-semibold">9. Governing Law</h4>
              <p>This agreement shall be governed by and construed in accordance with the laws of India.</p>
              <h4 className="font-semibold">10. Acknowledgment and Acceptance</h4>
              <p>By using the Vasstr platform to list and sell products, you acknowledge and agree to the above terms and conditions. These terms constitute a binding agreement between you (the Partner) and Vasstr.</p>
          <h4 className="font-semibold">Contact Information</h4>
          <p>If you have any questions or concerns about these terms & conditions, please contact us at <strong>Email:</strong> team@vasstr.com <strong>Phone:</strong> +91 9704387878</p>
        </div>
        <div className="flex justify-end border-t pt-4 mt-6">
          <Button
            type="button"
            onClick={() => setShowModal(false)}
            variant="primary"
          >
            Close
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default RegistrationForm;